import { useState, useEffect, useRef, useMemo } from "react";
import { useQuery, useConvex } from "convex/react";
import { api } from "../../../../convex/_generated/api";
import { Id } from "../../../../convex/_generated/dataModel";
import { Overlay, AspectRatio } from "../types";
import { hasAutosave, clearAutosave, loadEditorState } from "../utils/general/indexdb-helper";
import { resolveOverlaysOnLoad, needsUrlResolution } from "../utils/general/overlay-url-loader";

/**
 * Custom hook to load project state (overlays) from Convex via URL parameter.
 *
 * ## How it works:
 * 1. Reads a project ID from URL query parameters (e.g., ?projectId=456)
 * 2. Uses Convex useQuery to fetch project data with proper authentication
 * 3. Returns the overlays and aspect ratio to initialize the editor state
 * 4. If there's existing autosaved work, shows a confirmation modal
 *
 * ## Setup Required:
 * - `VITE_DISABLE_PROJECT_LOADING`: Set to 'true' to disable this feature
 *
 * ## Usage Example:
 * ```typescript
 * // In your React component:
 * const { overlays, aspectRatio, isLoading, showModal, onConfirmLoad, onCancelLoad } = 
 *   useProjectStateFromUrl('projectId', 'MyProject');
 *
 * // Wait for loading before rendering:
 * if (isLoading) return <div>Loading project...</div>;
 *
 * // Navigate to editor with project state:
 * window.location.href = `/editor?projectId=kh7awqzrgxycatwgfgtts0tt917yjv1r`;
 * ```
 *
 * @param paramName - Name of the URL parameter to read (e.g., 'projectId')
 * @param projectId - Project ID for autosave checking
 * @returns Object containing overlays, aspect ratio, loading state, and modal controls
 */
export function useProjectStateFromUrl(
  paramName: string = "projectId",
  projectId: string = "TestComponent"
): {
  overlays: Overlay[];
  aspectRatio: AspectRatio | null;
  isLoading: boolean;
  showModal: boolean;
  onConfirmLoad: () => void;
  onCancelLoad: () => void;
} {
  // Use empty array as fallback for new blank projects
  const fallbackOverlays: Overlay[] = [];
  const [overlays, setOverlays] = useState<Overlay[]>(fallbackOverlays);
  const [aspectRatio, setAspectRatio] = useState<AspectRatio | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showModal, setShowModal] = useState<boolean>(false);
  const [hasProcessedProject, setHasProcessedProject] = useState<boolean>(false);

  // Store project data when we need user confirmation
  const pendingProjectDataRef = useRef<{ overlays: Overlay[]; aspectRatio?: AspectRatio } | null>(null);

  // Use ref to avoid adding fallbackOverlays to dependency array
  const fallbackOverlaysRef = useRef(fallbackOverlays);

  // Get Convex client for URL resolution
  const convexClient = useConvex();

  // Update ref when fallbackOverlays change
  useEffect(() => {
    fallbackOverlaysRef.current = fallbackOverlays;
  }, [fallbackOverlays]);

  // Extract project ID from URL
  const projectIdFromUrl = useMemo(() => {
    if (typeof window === "undefined") return null;
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get(paramName);
  }, [paramName]);

  // Check if project loading is disabled
  const isDisabled = import.meta.env.VITE_DISABLE_PROJECT_LOADING === "true";

  // Use Convex query to fetch project data with proper authentication
  // Skip the query if disabled or no project ID in URL
  const projectData = useQuery(
    api.projects.getProject,
    !isDisabled && projectIdFromUrl 
      ? { projectId: projectIdFromUrl as Id<"projects"> }
      : "skip"
  );

  // Process project data when it arrives
  useEffect(() => {
    // Handle disabled state
    if (isDisabled) {
      console.log(
        "[useProjectStateFromUrl] Project loading is disabled via environment variable"
      );
      setOverlays(fallbackOverlaysRef.current);
      setIsLoading(false);
      return;
    }

    // No parameter in URL - use fallback
    if (!projectIdFromUrl) {
      console.log(
        `[useProjectStateFromUrl] No '${paramName}' parameter found, using fallback overlays`
      );
      setOverlays(fallbackOverlaysRef.current);
      setIsLoading(false);
      return;
    }

    // Still loading from Convex
    if (projectData === undefined) {
      console.log(
        `[useProjectStateFromUrl] Loading project '${projectIdFromUrl}' from Convex...`
      );
      return;
    }

    // Project not found or user doesn't have access
    if (projectData === null) {
      console.warn(
        `[useProjectStateFromUrl] Project '${projectIdFromUrl}' not found or access denied`
      );
      setOverlays(fallbackOverlaysRef.current);
      setIsLoading(false);
      return;
    }

    // Already processed this project data
    if (hasProcessedProject) {
      return;
    }

    // Process the project data
    const processProjectData = async () => {
      try {
        console.log(
          `[useProjectStateFromUrl] Successfully fetched project '${projectIdFromUrl}' from Convex`
        );

        // Parse overlays from Convex project data
        let projectOverlays: Overlay[] = [];
        if (projectData.editorOverlaysJson) {
          try {
            projectOverlays = JSON.parse(projectData.editorOverlaysJson);
            console.log(
              `[useProjectStateFromUrl] Loaded ${projectOverlays.length} overlays from Convex`
            );
          } catch (parseError) {
            console.error('[useProjectStateFromUrl] Failed to parse overlays from Convex:', parseError);
            projectOverlays = [];
          }
        }

        // Resolve any blob URLs or stale URLs in the loaded overlays
        // This fixes the issue where saved projects have blob URLs that don't work on reload
        if (projectOverlays.length > 0 && needsUrlResolution(projectOverlays)) {
          console.log('[useProjectStateFromUrl] Resolving stale media URLs...');
          const { overlays: resolvedOverlays, resolvedCount, failedCount } =
            await resolveOverlaysOnLoad(projectOverlays, convexClient);

          projectOverlays = resolvedOverlays;

          if (resolvedCount > 0) {
            console.log(
              `[useProjectStateFromUrl] Resolved ${resolvedCount} stale URL(s)`
            );
          }
          if (failedCount > 0) {
            console.warn(
              `[useProjectStateFromUrl] Failed to resolve ${failedCount} URL(s) - some media may not load`
            );
          }
        }

        // Get aspect ratio from Convex or use default
        const projectAspectRatio: AspectRatio =
          (projectData.editorAspectRatio as AspectRatio) || "9:16";

        console.log(
          `[useProjectStateFromUrl] Project aspect ratio: ${projectAspectRatio}`
        );

        // Get local autosave timestamp
        const localAutosaveTimestamp = await hasAutosave(projectId);

        // Get remote state timestamp from Convex project's updatedAt
        const remoteTimestamp = projectData.updatedAt || 0;

        console.log(
          `[useProjectStateFromUrl] Local autosave timestamp: ${localAutosaveTimestamp}, Remote timestamp: ${remoteTimestamp}`
        );

        // Only show modal if local is NEWER than remote
        const hasNewerLocalChanges = localAutosaveTimestamp !== null &&
          localAutosaveTimestamp > remoteTimestamp;

        if (hasNewerLocalChanges) {
          // Local has newer changes - show modal for user decision
          console.log(
            "[useProjectStateFromUrl] Local autosave is newer than remote, showing confirmation modal"
          );
          pendingProjectDataRef.current = {
            overlays: projectOverlays,
            aspectRatio: projectAspectRatio
          };
          setShowModal(true);
          setIsLoading(false);
        } else {
          // No conflict or remote is newer - auto-clear stale local and load from remote
          console.log(
            "[useProjectStateFromUrl] Remote is same or newer, loading from Convex"
          );
          if (localAutosaveTimestamp !== null) {
            // Clear stale local autosave
            await clearAutosave(projectId);
            console.log("[useProjectStateFromUrl] Cleared stale local autosave");
          }
          setOverlays(projectOverlays);
          setAspectRatio(projectAspectRatio);
          setIsLoading(false);
        }

        setHasProcessedProject(true);
      } catch (error) {
        console.error(
          `[useProjectStateFromUrl] Failed to process project:`,
          error
        );
        setOverlays(fallbackOverlaysRef.current);
        setIsLoading(false);
        setHasProcessedProject(true);
      }
    };

    processProjectData();
  }, [projectData, projectIdFromUrl, paramName, projectId, isDisabled, hasProcessedProject, convexClient]);

  // Handler for when user confirms they want to load the project
  const onConfirmLoad = async () => {
    if (pendingProjectDataRef.current) {
      console.log(
        "[useProjectStateFromUrl] User confirmed load, clearing autosave and loading project"
      );
      await clearAutosave(projectId);
      setOverlays(pendingProjectDataRef.current.overlays);
      if (pendingProjectDataRef.current.aspectRatio) {
        setAspectRatio(pendingProjectDataRef.current.aspectRatio);
      }
      pendingProjectDataRef.current = null;
    }
    setShowModal(false);
  };

  // Handler for when user cancels and wants to keep their work
  const onCancelLoad = async () => {
    console.log(
      "[useProjectStateFromUrl] User chose to keep existing work, loading from local autosave"
    );

    try {
      // Load local autosave state
      const localState = await loadEditorState(projectId);
      if (localState?.overlays) {
        setOverlays(localState.overlays);
        console.log(
          `[useProjectStateFromUrl] Loaded ${localState.overlays.length} overlays from local autosave`
        );
      }
      if (localState?.aspectRatio) {
        setAspectRatio(localState.aspectRatio);
      }
      // The Convex sync will happen automatically via useAutosave once overlays are set
    } catch (error) {
      console.error('[useProjectStateFromUrl] Failed to load local autosave:', error);
      // Fall back to empty state
      setOverlays(fallbackOverlaysRef.current);
    }

    pendingProjectDataRef.current = null;
    setShowModal(false);
  };

  return {
    overlays,
    aspectRatio,
    isLoading,
    showModal,
    onConfirmLoad,
    onCancelLoad,
  };
}
